<template>
  <v-container class="accounts-configuration">
    <v-row justify="center">
      <ViewHeader :title="viewTitle">
        <ui-button
          color="primary"
          rounded
          icon="icon-plus"
          style="min-width: 230px;"
          @click="handleAddAccountButtonClick"
          :loading="loadingData"
        >
          {{ $t("message.common.addAccount") }}
        </ui-button>
      </ViewHeader>

      <v-col cols="12">
        <template v-if="!accounts.length > 0">
          <v-card style="min-height: 650px;" class="d-flex">
            <div class="mx-auto d-flex align-center">
              <div class="no-data">
                <h2 class="subtitle mb-5">
                  {{ $t("message.companyProfile.noData.title") }}
                </h2>
                <p class="no-data__description">
                  {{ $t("message.companyProfile.noData.description") }}
                </p>
                <v-btn
                  color="secondary"
                  @click="handleAddAccountButtonClick"
                  :loading="loadingData"
                >
                  {{ $t("message.common.addAccount") }}
                </v-btn>
              </div>
            </div>
          </v-card>
        </template>
        <template v-else>
          <v-row no-gutters class="accounts">
            <v-col cols="12">
              <v-card style="min-height: 650px;" class="main py-8 px-8">
                <v-row no-gutters class="mb-10">
                  <v-col cols="12">
                    <h2
                      class="subtitle accounts__header-text"
                      :style="`border-color: ${colors.ACCENT}`"
                    >
                      {{
                        $t(
                          "message.companyProfile.listings.subtitle.mainSection"
                        )
                      }}
                    </h2>
                    <InfoBox
                      class="mt-2"
                      :text="
                        $t(
                          'message.companyProfile.listings.description.mainSection'
                        )
                      "
                    />
                    <InfoBox
                      class="mt-2"
                      v-if="isLastImportPending"
                      :text="
                        $t(
                          'message.companyProfile.listings.actionsDisabledDuringImport'
                        )
                      "
                    />

                    <LocationsImportSummary
                      v-if="lastImport"
                      :import-data="lastImport"
                      :refresh-pending="loadingImportStatus"
                      @refreshStatus="getLocationsImportStatus(true)"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-row no-gutters>
                      <v-col cols="4">
                        <p class="list-label">
                          {{ $t("message.common.accountType") }}
                        </p>
                      </v-col>
                      <v-col cols="4">
                        <p class="list-label">
                          {{ $t("message.common.userName") }}
                        </p>
                      </v-col>
                    </v-row>

                    <v-expansion-panels v-model="panel">
                      <v-expansion-panel
                        v-for="account in accounts"
                        :key="account.id"
                        class="expansion-panel"
                      >
                        <v-expansion-panel-header
                          class="account"
                          :disabled="isLastImportPending"
                        >
                          <v-row no-gutters>
                            <v-col cols="4" class="d-flex align-center">
                              <div class="account-name">
                                <v-icon color="primary">icon-google</v-icon>
                                <p>
                                  {{ $t("message.common.googleMyBusiness") }}
                                </p>
                              </div>
                            </v-col>
                            <v-col cols="4" class="pl-6 d-flex align-center">
                              <div>
                                <span @click.stop class="selectable-text">{{
                                  account.email
                                }}</span>
                              </div>
                            </v-col>
                            <div class="account__actions">
                              <ui-button
                                v-if="account.email !== 'system@center.ai'"
                                class="listings-btn"
                                color="primary"
                                rounded
                                outlined
                                icon="icon-plus"
                                @click="openPickProfileDialog(account.id)"
                                :loading="loadingButton"
                                :disabled="isLastImportPending"
                              >
                                {{ $t("message.common.addProfile") }}
                              </ui-button>
                              <v-menu
                                left
                                :offset-x="true"
                                origin="left top"
                                nudge-top="11"
                              >
                                <template #activator="{ on }">
                                  <v-icon
                                    class="group__more-icon mr-4"
                                    v-on="on"
                                    :disabled="isLastImportPending"
                                    >icon-more
                                  </v-icon>
                                </template>
                                <ui-button
                                  @click="openRemoveAccountDialog(account.id)"
                                  icon="icon-trash"
                                  iconColor="red"
                                  :disabled="loadingButton"
                                >
                                  {{ $t("message.common.removeAccount") }}
                                </ui-button>
                              </v-menu>
                            </div>
                          </v-row>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-list
                            class="profiles"
                            v-if="
                              account.profilesAdded &&
                                account.profilesAdded.length > 0
                            "
                          >
                            <v-list-item
                              v-for="profile in account.profilesAdded"
                              :key="profile.id"
                              class="profile"
                            >
                              <v-row no-gutters justify="space-between">
                                <v-col cols="4">
                                  <div class="d-flex flex-column">
                                    <span class="profile__title">
                                      {{ profile.profile_name }}
                                    </span>
                                    <span class="profile-info">
                                      {{
                                        $t(
                                          "message.common.importedBusinessAddresses"
                                        )
                                      }}:
                                      {{
                                        profile.active_imported_addresses_count
                                      }}
                                    </span>
                                  </div>
                                </v-col>
                                <div class="profile__actions">
                                  <ui-button
                                    rounded
                                    class="listings-btn"
                                    @click="chooseProfile(profile)"
                                    :loading="loadingButton"
                                    :disabled="isLastImportPending"
                                  >
                                    {{ $t("message.common.pickFacilities") }}
                                  </ui-button>
                                  <v-menu
                                    left
                                    :offset-x="true"
                                    origin="left top"
                                    nudge-top="11"
                                  >
                                    <template #activator="{ on }">
                                      <v-icon
                                        class="group__more-icon"
                                        v-on="on"
                                        :disabled="isLastImportPending"
                                      >
                                        icon-more
                                      </v-icon>
                                    </template>
                                    <ui-button
                                      rounded
                                      icon="icon-trash"
                                      iconColor="pink"
                                      @click="openDeleteFacilities(profile)"
                                      :disabled="loadingButton"
                                      :loading="loadingButton"
                                    >
                                      {{ $t("message.common.removeProfile") }}
                                    </ui-button>
                                  </v-menu>
                                </div>
                              </v-row>
                            </v-list-item>
                          </v-list>
                          <div v-else class="profiles profiles__no-data">
                            {{
                              $t(
                                "message.companyProfile.listings.noProfilesAdded"
                              )
                            }}
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <SectionLoader :status="loading" :opacity="1" />
      </v-col>

      <PickProfileDialog
        :profiles="selectedAccountProfilesToAdd"
        :isOpen="pickProfileDialog"
        :loadingButton="loadingButton"
        @close="closePickProfileDialog"
        @chooseProfile="chooseProfile($event)"
      />
      <BusinessAddressesDialog
        v-if="pickBusinessAddressesDialog"
        :businessAddresses="apiBusinessAddresses"
        :loadingImportButton="loadingModalImportButton"
        :isOpen="pickBusinessAddressesDialog"
        @handleClick="handleImportButtonClick($event)"
        @handleIgnoreUnverified="ignoreUnverifiedFacilities = $event"
        @close="pickBusinessAddressesDialog = false"
      />
      <DeleteBusinessAddressesDialog
        v-if="deleteBusinessAddressesDialog"
        :businessAddresses="{
          headers: apiBusinessAddresses.headers,
          items: selectedBusinessAddresses
        }"
        :loadingImportButton="loadingModalImportButton"
        :isOpen="deleteBusinessAddressesDialog"
        @handleClick="handleDeleteImportedButtonClick($event)"
        @close="deleteBusinessAddressesDialog = false"
      />
      <ConfirmationDialog
        :isOpen="confirmationDialog"
        :body="confirmationDialogBody"
        @closeDialog="confirmationDialog = false"
        @addAccount="handleAddAccount"
        @delete="onDelete($event)"
      />
    </v-row>
  </v-container>
</template>

<script>
import { APP_COLORS } from "@/config/app-colors";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import PickProfileDialog from "@/components/ListingsConfiguration/Dialog/PickProfile.vue";
import BusinessAddressesDialog from "@/components/ListingsConfiguration/Dialog/BusinessAddresses.vue";
import DeleteBusinessAddressesDialog from "@/components/ListingsConfiguration/Dialog/DeleteBusinessAddresses.vue";
import ConfirmationDialog from "@/components/ListingsConfiguration/Dialog/Confirmation.vue";
import LocationsImportSummary from "@/components/ListingsConfiguration/LocationsImportSummary.vue";
import InfoBox from "@/components/ui/InfoBox.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";

import { googleApiProfiles } from "@/helpers/mixins/google-api-profiles";
import { LOCATION_IMPORT_STATUS } from "@/enums/gmb-locations-import";

export default {
  name: "CompanyListingsConfiguration",
  mixins: [googleApiProfiles],
  components: {
    ViewHeader,
    SectionLoader,
    PickProfileDialog,
    BusinessAddressesDialog,
    DeleteBusinessAddressesDialog,
    ConfirmationDialog,
    LocationsImportSummary,
    InfoBox
  },
  data() {
    return {
      viewTitle: this.$t("message.companyProfile.listings.title"),
      loading: false,
      panel: 0
    };
  },
  computed: {
    colors() {
      return APP_COLORS;
    },
    isLastImportPending() {
      return (
        this.lastImport &&
        this.lastImport.status &&
        this.lastImport.status === LOCATION_IMPORT_STATUS.PENDING
      );
    }
  },
  async created() {
    await this.getGoogleAccounts();
    await this.getLocationsImportStatus();
  },
  beforeDestroy() {
    clearTimeout(this.locationsStatusTimeout);
    this.locationsStatusTimeout = null;
  }
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";
.accounts-configuration {
  .subtitle {
    @include font-size(16px);
    font-weight: 600;
    line-height: 32px;
  }

  .description {
    @include font-size(14px);
  }

  .no-data {
    max-width: 576px;
    text-align: center;
  }

  .no-data__description {
    @include font-size(14px);
    margin-bottom: 60px;
  }

  .accounts {
    > .aside {
      margin-top: $spacing * 4;
      @media #{map-get($display-breakpoints, 'xl-only')} {
        margin-top: 0;
        padding-left: 70px;
        padding-right: $spacing * 4;
      }
    }

    &__header-text {
      margin-bottom: $spacing * 2;
      line-height: 32px;
      padding-left: $spacing * 1.5;
      border-left-width: 3px;
      border-left-style: solid;
    }

    &__description-card {
      display: flex;
      align-items: flex-start;
      border-radius: 8px;
      background-color: $light-4;
      padding: $spacing * 1.5;
    }

    &__description-text {
      margin-bottom: 0;
    }
  }

  .account {
    @include font-size(14px);
    padding: $spacing * 3;

    &__actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: auto;
    }

    &:disabled {
      cursor: progress;
    }
  }

  .account-name {
    display: flex;
    align-items: center;

    > p {
      margin-left: $spacing * 3;
      margin-bottom: 0;
    }
  }

  .profiles {
    border-radius: 25px;
    padding: $spacing * 2 $spacing * 4.5;
    background-color: $light-4;

    &__no-data {
      @include font-size(14px);
    }
  }

  .profile {
    @include font-size(14px);
    padding: $spacing 0;

    &:not(:last-child) {
      border-bottom: 1px solid rgba($text, 0.1);
    }

    &__title {
      font-weight: 600;
    }

    &__actions {
      display: flex;
      align-items: center;
      margin-right: $spacing * 0.5;
    }
  }

  .list-label {
    @include font-size(14px);
    margin-left: $spacing * 3;
    margin-bottom: $spacing * 1.25;
    color: $gray-darker-2;
  }

  .listings-btn {
    margin-right: $spacing * 2;
    min-width: 180px;
  }

  .selectable-text {
    user-select: text;
    cursor: text;
  }
}

.expansion-panel {
  box-shadow: none;

  &::before {
    box-shadow: none;
  }

  &::after {
    content: none;
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba($text, 0.1);
  }
}
</style>
